// Core
import React, {
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { createRoot, type Root } from 'react-dom/client';

// Libraries
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { useTheme, ThemeProvider } from 'styled-components';
import _ from 'lodash';

// Config
import { BREAKPOINTS } from '../../config/screen';

// Plugins
import {
  DASHBOARD_NULLISH_TEXT,
  formatNumber,
} from '../../plugins/general';

// Types
import { Theme } from '../../types/theme';
import { Thresholds } from '../../types/global';

// Components
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../layout/elements/Paragraph';
import { ThresholdErrorTooltip } from '../../layout/elements/ThresholdErrorTooltip';

// Component
import {
  Container,
  Element,
  ProgressIndicatorsContainer,
  ProgressThermometer,
  StepsThermometer,
  TextContainer,
  ThermometerAntD,
} from './VibrationThermometer.style';
import { VibrationThermometerProps } from './VibrationThermometer.types';

function VibrationThermometer({
  className,
  decimalPlaces,
  maxValue,
  minValue,
  running = true,
  testId,
  thresholds,
  title,
  unit,
  value,
}: VibrationThermometerProps): JSX.Element | null {
  // Dependencies
  const theme: Theme = useTheme();
  const { i18n } = useTranslation();

  // Breakpoint detection
  const isMobile: boolean = useMediaQuery({ maxWidth: 375 });
  const isSmallDevice: boolean = useMediaQuery({ maxWidth: 428 });
  const isXsToSm: boolean = useMediaQuery({
    minWidth: 376,
    maxWidth: BREAKPOINTS.sm - 1,
  });
  const isTablet: boolean = useMediaQuery({
    minWidth: BREAKPOINTS.sm,
    maxWidth: BREAKPOINTS.md,
  });

  const stepsCount: number = useMemo((): number => {
    if (isMobile) return 31;
    if (isXsToSm) return 34;
    if (isTablet) return 39;
    return 43;
  }, [isMobile, isXsToSm, isTablet]);

  // Refs
  const thermometerRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const valueRootRef: React.MutableRefObject<Root | null> = useRef<Root | null>(null);
  const valueDivRef: React.MutableRefObject<HTMLElement | null> = useRef<HTMLElement | null>(null);

  /* ***********************************************************************************************
  ***************************************** METHODS ************************************************
  *********************************************************************************************** */

  // Validates thresholds and returns error message if invalid
  const thresholdValidationError = useMemo((): string | null => {
    if (!thresholds) return null;

    const fieldName: string = title ? `[${title}] ` : '';

    if (
      !_.isNil(thresholds.lowAlarm)
      && !_.isNil(thresholds.lowWarning)
      && thresholds.lowAlarm >= thresholds.lowWarning
    ) {
      return `${fieldName}[lowAlarm] must be less than [lowWarning] `
        + `(lowAlarm: ${thresholds.lowAlarm}, lowWarning: ${thresholds.lowWarning})`;
    }

    if (
      !_.isNil(thresholds.lowAlarm)
      && !_.isNil(thresholds.highWarning)
      && thresholds.lowAlarm >= thresholds.highWarning
    ) {
      return `${fieldName}[lowAlarm] must be less than [highWarning] `
        + `(lowAlarm: ${thresholds.lowAlarm}, highWarning: ${thresholds.highWarning})`;
    }

    if (
      !_.isNil(thresholds.lowAlarm)
      && !_.isNil(thresholds.highAlarm)
      && thresholds.lowAlarm >= thresholds.highAlarm
    ) {
      return `${fieldName}[lowAlarm] must be less than [highAlarm] `
        + `(lowAlarm: ${thresholds.lowAlarm}, highAlarm: ${thresholds.highAlarm})`;
    }

    if (
      !_.isNil(thresholds.lowWarning)
      && !_.isNil(thresholds.highWarning)
      && thresholds.lowWarning >= thresholds.highWarning
    ) {
      return `${fieldName}[lowWarning] must be less than [highWarning] `
        + `(lowWarning: ${thresholds.lowWarning}, highWarning: ${thresholds.highWarning})`;
    }

    if (
      !_.isNil(thresholds.lowWarning)
      && !_.isNil(thresholds.highAlarm)
      && thresholds.lowWarning >= thresholds.highAlarm
    ) {
      return `${fieldName}[lowWarning] must be less than [highAlarm] `
        + `(lowWarning: ${thresholds.lowWarning}, highAlarm: ${thresholds.highAlarm})`;
    }

    if (
      !_.isNil(thresholds.highWarning)
      && !_.isNil(thresholds.highAlarm)
      && thresholds.highWarning >= thresholds.highAlarm
    ) {
      return `${fieldName}[highWarning] must be less than [highAlarm] `
        + `(highWarning: ${thresholds.highWarning}, highAlarm: ${thresholds.highAlarm})`;
    }

    return null;
  }, [thresholds, title]);

  const highAlarm: number | null | undefined = thresholds?.highAlarm;
  const highWarning: number | null | undefined = thresholds?.highWarning;
  const lowAlarm: number | null | undefined = thresholds?.lowAlarm;
  const lowWarning: number | null | undefined = thresholds?.lowWarning;

  // Calculate percentage based on actual min/max values
  const percentage: number = _.isNil(value) || _.isNil(minValue) || _.isNil(maxValue)
    ? 0
    : ((value - minValue) / (maxValue - minValue)) * 100;

  // Calculates a threshold value's position relative to a defined range
  const calculateThreshold = useMemo(() => (
    thresholdValue: number | null | undefined,
    minimumValue: number | null | undefined,
    maximumValue: number | null | undefined,
  ): number => {
    let thresholdPosition: number = 0;

    if (
      !_.isNil(thresholdValue)
      && !_.isNil(minimumValue)
      && !_.isNil(maximumValue)
      && maximumValue !== minimumValue
    ) {
      const clampedValue: number = Math.max(
        Math.min(thresholdValue, maximumValue),
        minimumValue,
      );
      const relativeValue: number = clampedValue - minimumValue;
      const percent: number = relativeValue / (maximumValue - minimumValue);
      thresholdPosition = Math.round(percent * stepsCount);
    }

    return thresholdPosition;
  }, [stepsCount]);

  const thresholdsTick: Thresholds = useMemo((): Thresholds => ({
    highWarning: calculateThreshold(thresholds?.highWarning, minValue, maxValue),
    highAlarm: calculateThreshold(thresholds?.highAlarm, minValue, maxValue),
    lowWarning: calculateThreshold(thresholds?.lowWarning, minValue, maxValue),
    lowAlarm: calculateThreshold(thresholds?.lowAlarm, minValue, maxValue),
  }), [
    thresholds,
    minValue,
    maxValue,
    calculateThreshold,
  ]);

  // Determines the current thermometer color based on calculated percentages
  let currentColor: string;

  if (!running || thresholdValidationError) {
    currentColor = theme.color.vibrationThermometer.offProgressBackgroundColor;
  } else if (
    !_.isNil(value)
    && (
      (!_.isNil(highAlarm) && value >= highAlarm)
      || (!_.isNil(lowAlarm) && value <= lowAlarm)
    )
  ) {
    currentColor = theme.color.vibrationThermometer.alarmBackgroundColor;
  } else if (
    !_.isNil(value)
    && (
      (!_.isNil(highWarning) && value >= highWarning)
      || (!_.isNil(lowWarning) && value <= lowWarning)
    )
  ) {
    currentColor = theme.color.vibrationThermometer.warningBackgroundColor;
  } else {
    currentColor = theme.color.vibrationThermometer.successBackgroundColor;
  }

  // Determine CSS class for gradient styling
  const getThresholdClass = (): string => {
    let thresholdClass: string = 'success';

    if (!running || thresholdValidationError) {
      thresholdClass = 'off';
    } else {
      const isAlarm = !_.isNil(value) && (
        (!_.isNil(highAlarm) && value >= highAlarm)
        || (!_.isNil(lowAlarm) && value <= lowAlarm)
      );

      const isWarning = !_.isNil(value) && (
        (!_.isNil(highWarning) && value >= highWarning)
        || (!_.isNil(lowWarning) && value <= lowWarning)
      );

      if (isAlarm) {
        thresholdClass = 'alarm';
      } else if (isWarning) {
        thresholdClass = 'warning';
      }
    }

    return thresholdClass;
  };

  /* ***********************************************************************************************
  **************************************** COMPONENT HANDLING **************************************
  *********************************************************************************************** */

  // UseEffect to re-render on language change
  useEffect((): void => {
    // Force update component when language changes
  }, [i18n.language]);

  // Cleanup: unmount React root when component unmounts
  useEffect(() => () => {
    if (valueRootRef.current) {
      // Use setTimeout to avoid unmounting during React render
      setTimeout(() => {
        if (valueRootRef.current) {
          valueRootRef.current.unmount();
          valueRootRef.current = null;
        }
      }, 0);
    }
  }, []);

  // Create value container and handle rendering of value or error tooltip
  useEffect((): void => {
    const thermometerElement: HTMLElement | null = thermometerRef.current;
    if (thermometerElement) {
      const allStepsItems: NodeListOf<Element> = thermometerElement.querySelectorAll(
        '.ant-progress-steps-item',
      );

      // Clean up existing React root before removing containers
      if (valueRootRef.current) {
        try {
          valueRootRef.current.unmount();
        } catch (error: unknown) {
          // Ignore unmount errors
        }
        valueRootRef.current = null;
      }

      // Remove existing value-containers from all steps first
      const existingValueContainers: NodeListOf<Element> = thermometerElement.querySelectorAll(
        '.value-container',
      );
      existingValueContainers.forEach((container: Element) => {
        container.remove();
      });

      // Reset div ref when removing containers
      valueDivRef.current = null;

      // Finding the element for the current value in the middle of the steps
      const middleStepIndex: number = Math.floor(allStepsItems.length / 2);
      const middleStep: Element = allStepsItems[middleStepIndex];
      const valueDiv: HTMLElement = document.createElement('div');
      valueDiv.classList.add('value-container');
      middleStep.appendChild(valueDiv);
      valueDivRef.current = valueDiv;

      // If there's a threshold validation error, render the error tooltip
      if (thresholdValidationError) {
        // Create root only once and reuse it
        if (!valueRootRef.current) {
          valueRootRef.current = createRoot(valueDiv);
        }

        // Render the ThresholdErrorTooltip component
        if (valueRootRef.current) {
          valueRootRef.current.render(
            <ThemeProvider theme={theme}>
              <ThresholdErrorTooltip errorMessage={thresholdValidationError} />
            </ThemeProvider>,
          );
        }
      } else {
        // Normal value rendering
        const displayValue: string = _.isNil(value)
          ? DASHBOARD_NULLISH_TEXT
          : `${formatNumber(value, decimalPlaces)}${unit}`;

        // If there's a React root, unmount FIRST, then add text
        if (valueRootRef.current) {
          const rootToUnmount: Root = valueRootRef.current;
          valueRootRef.current = null;

          // Unmount asynchronously and add text after unmount completes
          queueMicrotask(() => {
            try {
              rootToUnmount.unmount();
              // Wait for unmount to complete before adding text
              queueMicrotask(() => {
                if (valueDiv && valueDiv.parentNode) {
                  valueDiv.textContent = displayValue;
                }
              });
            } catch (error: unknown) {
              // If unmount fails, still try to set text
              if (valueDiv && valueDiv.parentNode) {
                valueDiv.textContent = displayValue;
              }
            }
          });
        } else {
          // No React root, safe to set text directly
          valueDiv.textContent = displayValue;
        }
      }

      valueDivRef.current = valueDiv;
    }
  }, [
    decimalPlaces,
    i18n.language,
    stepsCount,
    theme,
    thresholdValidationError,
    unit,
    value,
  ]);

  // Create min and max value containers as children of .ant-progress-inner (the progress bar)
  useEffect((): void => {
    const thermometerElement: HTMLElement | null = thermometerRef.current;
    if (thermometerElement) {
      const progressInner: HTMLElement | null = thermometerElement.querySelector(
        '.ant-progress-line .ant-progress-inner',
      );

      if (progressInner) {
        const existingMaxContainers: NodeListOf<Element> = thermometerElement.querySelectorAll(
          '.max-value-container',
        );
        existingMaxContainers.forEach((container: Element) => {
          container.remove();
        });

        const existingMinContainers: NodeListOf<Element> = thermometerElement.querySelectorAll(
          '.min-value-container',
        );
        existingMinContainers.forEach((container: Element) => {
          container.remove();
        });

        const newMaxValueDiv: HTMLElement = document.createElement('div');
        newMaxValueDiv.classList.add('max-value-container');
        newMaxValueDiv.textContent = _.isNil(maxValue)
          ? DASHBOARD_NULLISH_TEXT
          : formatNumber(maxValue, decimalPlaces);
        progressInner.appendChild(newMaxValueDiv);

        const newMinValueDiv: HTMLElement = document.createElement('div');
        newMinValueDiv.classList.add('min-value-container');
        newMinValueDiv.textContent = _.isNil(minValue)
          ? DASHBOARD_NULLISH_TEXT
          : formatNumber(minValue, decimalPlaces);
        progressInner.appendChild(newMinValueDiv);
      }
    }
  }, [
    decimalPlaces,
    minValue,
    maxValue,
    i18n.language,
  ]);

  // Apply threshold classes to steps
  useEffect((): void => {
    const thermometerElement: HTMLElement | null = thermometerRef.current;
    if (thermometerElement) {
      // Remove existing threshold classes from all steps
      const allSteps: NodeListOf<Element> = thermometerElement.querySelectorAll(
        '.ant-progress-steps-item',
      );
      allSteps.forEach((step: Element): void => {
        step.classList.remove('high-alarm', 'high-warning', 'low-alarm', 'low-warning');
      });

      // High Alarm
      const highAlarmIndicator: HTMLElement | null = thermometerElement.querySelector(
        `.ant-progress-steps-item:nth-child(${thresholdsTick.highAlarm})`,
      );
      highAlarmIndicator?.classList.add('high-alarm');

      // High Warning
      const highWarningIndicator: HTMLElement | null = thermometerElement.querySelector(
        `.ant-progress-steps-item:nth-child(${thresholdsTick.highWarning})`,
      );
      highWarningIndicator?.classList.add('high-warning');

      // Low Alarm
      const lowAlarmIndicator: HTMLElement | null = thermometerElement.querySelector(
        `.ant-progress-steps-item:nth-child(${thresholdsTick.lowAlarm})`,
      );
      lowAlarmIndicator?.classList.add('low-alarm');

      // Low Warning
      const lowWarningIndicator: HTMLElement | null = thermometerElement.querySelector(
        `.ant-progress-steps-item:nth-child(${thresholdsTick.lowWarning})`,
      );
      lowWarningIndicator?.classList.add('low-warning');
    }
  }, [thresholdsTick, stepsCount]);

  return (
    <div className={className} ref={thermometerRef}>
      <Element
        data-testid={testId}
        className={className}
        value={value}
        thresholds={thresholds}
        running={running}
      >
        <Container>
          <ProgressIndicatorsContainer>
            <ProgressThermometer
              data-min-value={
                _.isNil(minValue)
                  ? DASHBOARD_NULLISH_TEXT
                  : formatNumber(minValue, decimalPlaces)
              }
              data-max-value={
                _.isNil(maxValue)
                  ? DASHBOARD_NULLISH_TEXT
                  : formatNumber(maxValue, decimalPlaces)
              }
              className={getThresholdClass()}
            >
              <ThermometerAntD
                strokeLinecap="square"
                percent={percentage}
                showInfo={false}
                strokeColor={currentColor}
              />
            </ProgressThermometer>
            <StepsThermometer
              threshold={thresholdsTick}
              running={running}
              hasValidationError={!!thresholdValidationError}
            >
              <ThermometerAntD
                steps={stepsCount}
                showInfo={false}
                size="small"
              />
            </StepsThermometer>
          </ProgressIndicatorsContainer>
          <TextContainer>
            <Paragraph
              size={isSmallDevice ? ParagraphSize.xs : ParagraphSize.sm}
              weight={ParagraphWeight.bold}
              color={theme.color.vibrationThermometer.titleTextColor}
            >
              {title}
            </Paragraph>
          </TextContainer>
        </Container>
      </Element>
    </div>
  );
}

export { VibrationThermometer };
